import { Icon } from '@iconify/react'
import { useStorefrontContent, useBranding } from '@/branding/BrandingProvider'


// Precious-metal smart ring: a machined band (champagne gradient with edge
// highlights) + the arc-shaped inner sensor strip from the heirloom story, and
// an elegant biometric readout. Rendered in place of the watch under the luxury skin.
function LuxuryRingVisual({ hero }: { hero: ReturnType<typeof useStorefrontContent>['hero'] }) {
  return (
    <div className="flex-1 w-full flex flex-col items-center lg:items-end relative reveal-scale delay-300">
      <div className="lux-hero-glow" />
      {hero.image ? (
        <div className="relative">
          {/* concentric metal guides behind the product shot */}
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[440px] h-[440px] rounded-full" style={{ border: '1px solid var(--lux-line-soft)' }} />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[560px] h-[560px] rounded-full" style={{ border: '1px solid var(--lux-line-soft)', opacity: 0.5 }} />
          <img
            src={hero.image}
            alt="Crownring titanium smart ring"
            className="relative z-10 w-[420px] max-w-full animate-float rounded-lg"
            style={{ filter: 'drop-shadow(0 50px 70px rgba(0,0,0,0.55))' }}
          />
        </div>
      ) : (
      <div className="relative animate-float" style={{ filter: 'drop-shadow(0 40px 60px rgba(0,0,0,0.45))' }}>
        <svg viewBox="0 0 340 340" width="360" height="360" role="img" aria-label="Crownring precious-metal smart ring">
          <defs>
            <linearGradient id="lux-band" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%" stopColor="#a8894f" />
              <stop offset="42%" stopColor="#f4e7c6" />
              <stop offset="62%" stopColor="#c9ad72" />
              <stop offset="100%" stopColor="#8f7443" />
            </linearGradient>
            <radialGradient id="lux-inner" cx="50%" cy="42%" r="60%">
              <stop offset="0%" stopColor="#1c1a16" />
              <stop offset="100%" stopColor="#0c0b09" />
            </radialGradient>
          </defs>
          {/* concentric guide rings */}
          <circle cx="170" cy="170" r="158" fill="none" stroke="#e8d6a8" strokeOpacity="0.12" />
          <circle cx="170" cy="170" r="138" fill="none" stroke="#e8d6a8" strokeOpacity="0.08" />
          {/* the machined band */}
          <circle cx="170" cy="170" r="104" fill="none" stroke="url(#lux-band)" strokeWidth="40" />
          {/* inner well */}
          <circle cx="170" cy="170" r="84" fill="url(#lux-inner)" />
          {/* edge highlights */}
          <circle cx="170" cy="170" r="84" fill="none" stroke="#fff" strokeOpacity="0.22" strokeWidth="1.5" />
          <circle cx="170" cy="170" r="124" fill="none" stroke="#000" strokeOpacity="0.28" strokeWidth="1.5" />
          {/* arc sensor strip (~90% of inner circumference), skin-side */}
          <circle cx="170" cy="170" r="92" fill="none" stroke="#6f6250" strokeWidth="5" strokeLinecap="round" strokeDasharray="520 58" transform="rotate(20 170 170)" />
          <circle cx="170" cy="170" r="92" fill="none" stroke="#c9ad72" strokeOpacity="0.5" strokeWidth="1.5" strokeDasharray="4 10" transform="rotate(20 170 170)" />
        </svg>
      </div>
      )}
      {/* biometric readout */}
      <div className="mt-2 flex items-center gap-8 text-center">
        <div>
          <div className="lux-serif text-3xl" style={{ color: 'var(--lux-gold)' }}>{hero.watchStats.accuracy}</div>
          <div className="text-[0.68rem] uppercase tracking-[0.28em]" style={{ color: 'var(--lux-faint)' }}>{hero.watchStats.label.replace('\n', ' ')}</div>
        </div>
        <span className="w-px h-8" style={{ background: 'var(--lux-line)' }} />
        <div>
          <div className="lux-serif text-2xl" style={{ color: 'var(--lux-fg)' }}>{hero.watchStats.heartRate}</div>
          <div className="text-[0.68rem] uppercase tracking-[0.28em]" style={{ color: 'var(--lux-faint)' }}>Resting</div>
        </div>
        <span className="w-px h-8" style={{ background: 'var(--lux-line)' }} />
        <div>
          <div className="lux-serif text-2xl" style={{ color: 'var(--lux-fg)' }}>{hero.watchStats.spo2}</div>
          <div className="text-[0.68rem] uppercase tracking-[0.28em]" style={{ color: 'var(--lux-faint)' }}>Blood O₂</div>
        </div>
      </div>
    </div>
  )
}

export function HeroSection() {
  const { hero } = useStorefrontContent()
  const { skin } = useBranding()
  const isLuxury = skin === 'luxury'

  return (
    <section className="overflow-hidden bg-slate-50/50 pt-24 pb-32 relative">
      {isLuxury && <div className="lux-hero-glow" />}
      <div className="max-w-7xl mx-auto px-6 lg:px-8 flex flex-col lg:flex-row items-center gap-16">
        <div className="flex-1 relative z-10 text-center lg:text-left">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white border border-slate-200 text-sm font-medium text-slate-600 mb-6 reveal-fade">
            <span className="w-2 h-2 rounded-full bg-rust animate-pulse" />
            {hero.badge}
          </div>
          <h1 className="text-5xl lg:text-6xl font-medium tracking-tight text-navy leading-tight mb-6 reveal-base">
            {hero.title}
          </h1>
          <p className="text-lg text-slate-600 leading-relaxed mb-8 max-w-2xl mx-auto lg:mx-0 font-light reveal-base delay-100">
            {hero.subtitle}
          </p>
          <div className="flex flex-col sm:flex-row items-center gap-4 justify-center lg:justify-start reveal-base delay-200">
            <a
              href="#pricing"
              className="w-full sm:w-auto bg-rust text-white text-base font-medium px-8 py-4 rounded-full hover:bg-rust-dark transition-all shadow-lg shadow-rust/20 flex items-center justify-center gap-2"
            >
              {hero.cta}
              <Icon icon="solar:arrow-right-linear" />
            </a>
            <button className="w-full sm:w-auto bg-white text-navy border border-slate-200 text-base font-medium px-8 py-4 rounded-full hover:bg-slate-50 transition-colors flex items-center justify-center gap-2">
              <Icon icon="solar:play-circle-linear" className="text-xl" />
              {hero.secondaryCta}
            </button>
          </div>
          {/* Android app download — data-driven (admin Marketing Content, per tenant).
              Shown on both brands when appDownloadUrl is set; styled per skin so the
              luxury (Crownring) storefront gets a gold pill, default gets navy. Each
              brand's tenant config supplies its OWN apk URL. */}
          {hero.appDownloadUrl ? (
            <div className="mt-5 reveal-base delay-300 flex justify-center lg:justify-start">
              <a
                href={hero.appDownloadUrl}
                aria-label="Download the Android app (APK)"
                className={
                  'w-full sm:w-auto text-base font-medium px-8 py-4 rounded-full transition-all shadow-lg flex items-center justify-center gap-3 ' +
                  (isLuxury
                    ? 'hover:opacity-90'
                    : 'bg-navy text-white hover:bg-navy/90 shadow-navy/20')
                }
                style={isLuxury ? { background: 'var(--lux-gold, #C9A227)', color: '#1F2937' } : undefined}
              >
                <Icon
                  icon="mdi:android"
                  className="text-2xl"
                  style={{ color: isLuxury ? '#1F2937' : '#3ddc84' }}
                />
                <span>
                  {hero.appDownloadLabel || 'Download Android App'}
                  <span className={isLuxury ? 'ml-1.5 text-sm opacity-70' : 'ml-1.5 text-sm text-white/60'}>APK</span>
                </span>
              </a>
            </div>
          ) : null}
        </div>

        {/* Hero Visual: luxury ring (branded) or the default smartwatch */}
        {isLuxury ? (
          <LuxuryRingVisual hero={hero} />
        ) : (
        <div className="flex-1 w-full flex justify-center lg:justify-end relative reveal-scale delay-300">
          {/* Decorative rings */}
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[400px] h-[400px] border border-slate-200 rounded-full" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[300px] h-[300px] border border-slate-200 rounded-full" />

          {/* Watch UI */}
          <div className="w-[280px] h-[320px] bg-navy rounded-[3rem] shadow-2xl relative border-[8px] border-slate-900 flex flex-col items-center justify-center p-6 animate-float z-10 overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-b from-white/10 to-transparent" />

            <div className="relative z-10 flex flex-col items-center">
              <Icon icon="solar:shield-check-linear" className="text-rust text-4xl mb-3 animate-pulse-ring rounded-full" />
              <div className="text-rust text-5xl font-medium tracking-tight leading-none mb-1">
                {hero.watchStats.accuracy}
              </div>
              <div className="text-white text-sm font-light text-center opacity-80 uppercase tracking-widest mt-2 whitespace-pre-line">
                {hero.watchStats.label}
              </div>
            </div>

            {/* Vitals strip */}
            <div className="absolute bottom-6 w-full px-6 flex justify-between items-center z-10">
              <div className="flex flex-col items-center">
                <Icon icon="solar:heart-pulse-linear" className="text-white/50 text-sm mb-1" />
                <span className="text-white text-xs font-medium">{hero.watchStats.heartRate}</span>
              </div>
              <div className="w-[1px] h-6 bg-white/20" />
              <div className="flex flex-col items-center">
                <Icon icon="solar:wind-linear" className="text-white/50 text-sm mb-1" />
                <span className="text-white text-xs font-medium">{hero.watchStats.spo2}</span>
              </div>
            </div>
          </div>
        </div>
        )}
      </div>
    </section>
  )
}
